import React from 'react';
import { Link, useForm } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { FiArrowLeft, FiCalendar, FiUser, FiHome, FiMapPin, FiEdit2 } from 'react-icons/fi';

export default function TourShow({ tour, user }) {
  const { data, setData, patch, processing, errors } = useForm({
    status: tour.status || 'pending',
    notes: tour.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    patch(`/facility_owner/tours/${tour.id}`);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Button variant="outline" className="mb-4" asChild>
              <Link href="/facility_owner/tours">
                <FiArrowLeft className="mr-2 h-4 w-4" />
                Back to Tours
              </Link>
            </Button>
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Tour Details</h1>
                <p className="text-gray-500">
                  {formatDate(tour.scheduled_at)} at {formatTime(tour.scheduled_at)}
                </p>
              </div>
              <Badge 
                className="mt-2 md:mt-0" 
                variant={
                  tour.status === 'pending' ? 'secondary' :
                  tour.status === 'confirmed' ? 'success' :
                  tour.status === 'completed' ? 'default' :
                  'destructive'
                }
              >
                {tour.status.charAt(0).toUpperCase() + tour.status.slice(1)}
              </Badge>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Tour Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Facility</h3>
                    <p className="mt-1 flex items-center">
                      <FiHome className="mr-2 h-4 w-4 text-gray-400" />
                      {tour.facility?.name || 'Unknown facility'}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Visitor</h3>
                    <p className="mt-1 flex items-center">
                      <FiUser className="mr-2 h-4 w-4 text-gray-400" />
                      {tour.user?.email || 'Anonymous visitor'}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Date & Time</h3>
                    <p className="mt-1 flex items-center">
                      <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
                      {formatDate(tour.scheduled_at)} at {formatTime(tour.scheduled_at)}
                    </p>
                  </div>
                  
                  {tour.notes && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Notes</h3>
                      <p className="mt-1 p-3 bg-gray-50 rounded-md">{tour.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Update Tour</CardTitle>
                  <CardDescription>Change the status or add notes to this tour</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={data.status}
                        onValueChange={(value) => setData('status', value)}
                      >
                        <SelectTrigger className={errors.status ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={data.notes}
                        onChange={e => setData('notes', e.target.value)}
                        rows={4}
                        className={errors.notes ? 'border-red-500' : ''}
                        placeholder="Add any notes about this tour..."
                      />
                      {errors.notes && <p className="text-red-500 text-sm mt-1">{errors.notes}</p>}
                    </div>
                    
                    <div className="flex justify-end pt-2">
                      <Button type="submit" disabled={processing}>
                        Update Tour
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
            
            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link href={`/facility_owner/facilities/${tour.facility?.id}`}>
                      <FiHome className="mr-2 h-4 w-4" />
                      View Facility
                    </Link>
                  </Button>
                  
                  {tour.user && (
                    <Button className="w-full justify-start" variant="outline" asChild>
                      <Link href={`/facility_owner/leads?user=${tour.user.id}`}>
                        <FiUser className="mr-2 h-4 w-4" />
                        View User Leads
                      </Link>
                    </Button>
                  )}
                  
                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link href="/facility_owner/tour_slots/new">
                      <FiCalendar className="mr-2 h-4 w-4" />
                      Add New Tour Slot
                    </Link>
                  </Button>
                </CardContent>
              </Card>
              
              {tour.status === 'pending' && (
                <div className="mt-6">
                  <Button 
                    className="w-full" 
                    variant="success"
                    onClick={() => {
                      setData('status', 'confirmed');
                      patch(`/facility_owner/tours/${tour.id}`);
                    }}
                  >
                    Confirm Tour
                  </Button>
                </div>
              )}
              
              {(tour.status === 'pending' || tour.status === 'confirmed') && (
                <div className="mt-3">
                  <Button 
                    className="w-full" 
                    variant="destructive"
                    onClick={() => {
                      setData('status', 'cancelled');
                      patch(`/facility_owner/tours/${tour.id}`);
                    }}
                  >
                    Cancel Tour
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
